import {Component, Input} from '@angular/core';

/**
 * Notification / message component.
 *
 * Sizes (via the `size` input):
 *  - `large`  : 72px tall card with icon, bold title and message (needs `title`).
 *  - `medium` : default. Card with icon and message only (no title).
 *  - `small`  : no background/border, just the colored icon and the text.
 *
 * Types (via the `type` input): `info` (default), `warning`, `success`, `danger`.
 * The legacy value `alert` is still accepted and mapped to `warning`.
 */
@Component({
  selector: 'gp-message',
  template: `
    <div class="gp-message"
         [ngClass]="['gp-message--' + normalizedType, 'gp-message--' + normalizedSize]">
      <i class="gp-message__icon fa" [ngClass]="iconClass" aria-hidden="true"></i>
      <div class="gp-message__body">
        <strong *ngIf="normalizedSize === 'large' && title" class="gp-message__title">
          {{ title | translate }}
        </strong>
        <span *ngIf="text" class="gp-message__text">
          <ng-container *ngIf="!key">{{ text | translate }}</ng-container>
          <ng-container *ngIf="key">{{ text | translate:{key: key} }}</ng-container>
        </span>
        <ng-content></ng-content>
      </div>
    </div>`,
  // Styles are kept inline (instead of an external .scss) so they are always
  // bundled with the component. Colors are the SD Worx Ignite tokens.
  styles: [`
    :host { display: block; }

    .gp-message {
      display: flex;
      align-items: center;
      gap: 12px;
      border: 1px solid transparent;
      border-left-width: 4px;
      border-radius: 4px;
      padding: 12px 16px;
    }

    .gp-message__icon {
      flex: 0 0 auto;
      font-size: 18px;
      line-height: 1.4;
    }

    /* Icon + text on the same row; only the large size stacks title over text. */
    .gp-message__body {
      display: flex;
      align-items: baseline;
      gap: 6px;
      min-width: 0;
    }

    .gp-message__title { font-weight: 700; color: #323334; }
    .gp-message__text { color: #5a5b5c; }

    /* ----- Types (background + border + icon color) ----- */
    .gp-message--info { background-color: #f1f9ff; border-color: #006aff; }
    .gp-message--info .gp-message__icon { color: #006aff; }

    .gp-message--warning { background-color: #fff7ea; border-color: #f3b01d; }
    .gp-message--warning .gp-message__icon { color: #f3b01d; }

    .gp-message--success { background-color: #f1fcf0; border-color: #007900; }
    .gp-message--success .gp-message__icon { color: #007900; }

    .gp-message--danger { background-color: #fff3fa; border-color: #e90040; }
    .gp-message--danger .gp-message__icon { color: #e90040; }

    /* ----- Sizes ----- */
    .gp-message--large {
      min-height: 72px;
      padding: 14px 18px;
    }
    .gp-message--large .gp-message__icon { font-size: 20px; }
    /* Large stacks the bold title above the text. */
    .gp-message--large .gp-message__body {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }

    /* Medium is the default and inherits the card look above. */
    .gp-message--medium .gp-message__text { font-weight: 500; }

    /* Small: no background/border, just the colored icon and text. */
    .gp-message--small {
      background: transparent;
      border: none;
      border-radius: 0;
      padding: 2px 0;
      gap: 8px;
    }
    .gp-message--small .gp-message__text { color: #323334; }
  `]
})

export class MessageComponent {
  @Input() text: string;
  @Input() type: string;
  /** Bold title, only displayed in the `large` size. */
  @Input() title: string;
  /** `small` | `medium` | `large`. Defaults to `medium`. */
  @Input() size = 'medium';
  @Input() key: any;

  get normalizedType(): string {
    switch (this.type) {
      case 'alert':
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'danger':
        return 'danger';
      default:
        return 'info';
    }
  }

  get normalizedSize(): string {
    return this.size === 'small' || this.size === 'large' ? this.size : 'medium';
  }

  get iconClass(): string {
    switch (this.normalizedType) {
      case 'warning':
        return 'fa-exclamation-triangle';
      case 'success':
        return 'fa-check-circle';
      case 'danger':
        return 'fa-diamond-exclamation';
      default:
        return 'fa-info-circle';
    }
  }
}
